import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import { sortBy } from "parog-web/utils/sort";
import tWithDefault from "parog-web/utils/t-with-default";
import type BloodPressureSessionStartedJournalEvent from "./blood-pressure-session-started-journal-event";

const SPLIT_DAY_HOUR = 12;
export const SystolicLoinc = "8480-6";
export const DiastolicLoinc = "8462-4";

//---- Automatically generated, don't update this part ----

type SourceStringEnum = "withings";

export default class BloodPressureMeasuredJournalEvent extends JournalEvent {
  static type = "blood-pressure-measured-journal-event";

  @attr("number") declare systolicPressure: number;

  @attr("number") declare diastolicPressure: number;

  @attr("number") declare heartRate: number;

  @attr("string") declare source: SourceStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare isComputedAverage: boolean;

  @belongsTo("blood-pressure-session-started-journal-event", {
    async: false,
    inverse: "bloodPressureMeasuredJournalEvents"
  })
  declare bloodPressureSessionStartedJournalEvent: BloodPressureSessionStartedJournalEvent;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.blood-pressure-measured-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ],
      systolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.blood-pressure-measured-journal-event.systolic-pressure"
        )
      }),
      diastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.blood-pressure-measured-journal-event.diastolic-pressure"
        )
      }),
      heartRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.blood-pressure-measured-journal-event.heart-rate"
        )
      }),
      source: validateInclusion({
        list: ["withings"],
        allowBlank: true,
        description: intl.t(
          "validations.blood-pressure-measured-journal-event.source"
        )
      })
    };
  }

  //---- End of generated code ----

  static get SPLIT_DAY_HOUR(): number {
    return SPLIT_DAY_HOUR;
  }

  get isAlertingJournalEvent(): boolean {
    return this.bloodPressureSessionStartedJournalEvent.isAlertingJournalEvent;
  }

  /**
   * returns The day the measure belongs to, based on the begining of the session
   */
  get sessionDay(): number {
    return this.bloodPressureSessionStartedJournalEvent.getSessionDay(
      this.occurredAt
    );
  }

  get isMorning(): boolean {
    return dayjs(this.occurredAt).get("hour") < SPLIT_DAY_HOUR;
  }

  /**
   * Return the index of this measure by morning or evening.
   */
  get indexByMoment(): number {
    const isMorning = this.isMorning;
    return (
      this.bloodPressureSessionStartedJournalEvent
        .getMeasurementsOfSessionDay(this.sessionDay)
        .filter(je => je.isMorning === isMorning)
        .toSorted(sortBy("occurredAt"))
        .indexOf(this) + 1
    );
  }

  get isEvening(): boolean {
    return !this.isMorning;
  }

  hasAlertForCode(code: "8480-6" | "8462-4"): boolean {
    if (!code || !this.alertTriggeredJournalEvents.length) return false;

    return Boolean(
      this.alertTriggeredJournalEvents.find(alert => {
        return alert.code == code;
      })
    );
  }

  // ------------------------------
  // ---------- HELPERS -----------
  // ------------------------------

  /**
   * Create a date corresponding to the current session day and the moment of the day
   * @param sessionDay - The day of the session => 0, 1 or 2
   * @param measureIndex - Used to sort the measure of a session
   */
  static createMeasurementDate(
    startSessionDate: Date,
    sessionDay: number,
    isMorning: boolean,
    measureIndex = 0
  ): Date {
    const baseDate = dayjs(startSessionDate).add(sessionDay, "day");
    return isMorning
      ? dayjs(baseDate)
          .startOf("day")
          .add(1, "hour")
          .add(measureIndex, "minute")
          .toDate()
      : dayjs(baseDate)
          .endOf("day")
          .subtract(1, "hour")
          .subtract(2 - measureIndex, "minute")
          .toDate();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "blood-pressure-measured-journal-event": BloodPressureMeasuredJournalEvent;
  }
}
